/**
 * MealScanStation - Dedicated meal scan station for dining access control
 * Features:
 * - QR/badge scanning for meal verification
 * - Visual green/red access indicators
 * - Current meal slot detection
 * - Scan history and statistics
 * - Offline support
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getToken } from '@/lib/authToken';
import { 
  Camera, CameraOff, Volume2, VolumeX, X, 
  CheckCircle, XCircle, RefreshCw, AlertTriangle,
  UtensilsCrossed, Coffee, Sun, Moon, Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MealScanStation = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState({ breakfast: 0, lunch: 0, dinner: 0, total_today: 0 });
  const [recentScans, setRecentScans] = useState([]);
  const [error, setError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [selectedMealType, setSelectedMealType] = useState('lunch');
  const [selectedDiningZone, setSelectedDiningZone] = useState('main');
  const [cameraList, setCameraList] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  
  const html5QrCodeRef = useRef(null);
  const scannerContainerId = 'meal-scanner-container';

  // Get current meal time
  const getCurrentMealType = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 10) return 'breakfast';
    if (hour >= 11 && hour < 15) return 'lunch';
    if (hour >= 17 && hour < 21) return 'dinner';
    return 'lunch'; // Default
  };

  useEffect(() => {
    // Set initial meal type based on current time
    setSelectedMealType(getCurrentMealType());
    
    // Get cameras
    Html5Qrcode.getCameras().then(cameras => {
      setCameraList(cameras);
      const backCamera = cameras.find(c => 
        c.label.toLowerCase().includes('back') || 
        c.label.toLowerCase().includes('environment')
      );
      setSelectedCamera(backCamera?.id || cameras[0]?.id);
    }).catch(console.error);

    fetchEvent();
    fetchStats();
    fetchRecentScans();
    
    const interval = setInterval(() => {
      fetchStats();
      fetchRecentScans();
    }, 15000);
    
    return () => {
      clearInterval(interval);
      stopScanner();
    };
  }, [eventId]);

  // iter-438c: removed local shadow `const getToken = () => getToken();`
  // which infinite-recursed and threw RangeError before any scan fetch.
  // Uses the imported getToken from '@/lib/authToken' (line 17).

  const fetchEvent = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}`);
      if (res.ok) setEvent(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/meal-scan/stats`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchRecentScans = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/meal-scan/history?limit=10`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) setRecentScans(await res.json());
    } catch (e) { console.error(e); }
  };

  const playSound = (type) => {
    if (!soundEnabled) return;
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    if (type === 'success') {
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
    } else {
      osc.frequency.value = 330;
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    }
    
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  };

  const startScanner = async () => {
    if (!selectedCamera) {
      setError('No camera available');
      return;
    }
    
    setError(null);
    setResult(null);
    setScanning(true);

    try {
      html5QrCodeRef.current = new Html5Qrcode(scannerContainerId);
      await html5QrCodeRef.current.start(
        selectedCamera,
        { fps: 15, qrbox: { width: 250, height: 250 } },
        handleScanResult,
        () => {}
      );
    } catch (err) {
      console.error('Scanner start failed:', err);
      setError('Failed to start camera. Please check permissions.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        if (html5QrCodeRef.current.isScanning) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
      } catch (e) { console.error(e); }
    }
    setScanning(false);
  };

  const handleScanResult = useCallback(async (decodedText) => {
    // Pause scanner
    if (html5QrCodeRef.current) {
      try { await html5QrCodeRef.current.pause(); } catch (e) {}
    }

    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}/meal-scan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          qr_data: decodedText,
          meal_type: selectedMealType,
          dining_zone: selectedDiningZone
        })
      });

      const data = await res.json();
      
      if (data.success && data.allowed) {
        playSound('success');
        setResult({
          success: true,
          allowed: true,
          ...data
        });
        fetchStats();
        fetchRecentScans();
      } else {
        playSound('error');
        setResult({
          success: false,
          allowed: false,
          ...data
        });
      }
    } catch (err) {
      playSound('error');
      setResult({
        success: false,
        allowed: false,
        message: 'Network error. Please try again.',
        color: 'red'
      });
    }

    // Auto-resume after 4 seconds
    setTimeout(() => {
      setResult(null);
      if (html5QrCodeRef.current && scanning) {
        try { html5QrCodeRef.current.resume(); } catch (e) {}
      }
    }, 4000);
  }, [eventId, selectedMealType, selectedDiningZone, scanning]);

  const getMealIcon = (type) => {
    switch (type) {
      case 'breakfast': return <Coffee className="h-5 w-5" />;
      case 'lunch': return <Sun className="h-5 w-5" />;
      case 'dinner': return <Moon className="h-5 w-5" />;
      default: return <UtensilsCrossed className="h-5 w-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid="meal-scan-station">
      {/* Header */}
      <div className="bg-orange-600 p-4 flex justify-between items-center">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <UtensilsCrossed className="h-6 w-6" />
            {event?.name || 'Meal Scan Station'}
          </h1>
          <div className="flex items-center gap-2 text-orange-100 text-sm">
            <span>Dining Access Control</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="text-white border-orange-300 hover:bg-orange-700"
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-white border-orange-300 hover:bg-orange-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-80px)]">
        {/* Scanner Area */}
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          {/* Meal Type Selection */}
          <div className="mb-6 flex gap-4 flex-wrap justify-center">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">Meal Type:</label>
              <Select value={selectedMealType} onValueChange={setSelectedMealType}>
                <SelectTrigger className="w-36 bg-gray-800 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="breakfast">🍳 Breakfast</SelectItem>
                  <SelectItem value="lunch">🥗 Lunch</SelectItem>
                  <SelectItem value="dinner">🍽️ Dinner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">Dining Zone:</label>
              <Select value={selectedDiningZone} onValueChange={setSelectedDiningZone}>
                <SelectTrigger className="w-36 bg-gray-800 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="main">Main Hall</SelectItem>
                  <SelectItem value="vip">VIP Dining</SelectItem>
                  <SelectItem value="staff">Staff Area</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {!scanning ? (
            <div className="text-center">
              <div className="relative inline-block mb-8">
                <div className="w-32 h-32 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center">
                  <UtensilsCrossed className="h-16 w-16 text-white" />
                </div>
              </div>
              <h2 className="text-3xl font-bold mb-4">Ready to Scan</h2>
              <p className="text-gray-400 mb-8 max-w-sm">
                Scan delegate badge to verify {selectedMealType} entitlement
              </p>
              <Button 
                onClick={startScanner} 
                size="lg"
                className="text-xl px-8 py-6 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
                data-testid="start-meal-scanner-btn"
              >
                <Camera className="h-6 w-6 mr-2" />
                Start Scanner
              </Button>
            </div>
          ) : (
            <div className="relative w-full max-w-lg">
              <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl">
                <div id={scannerContainerId} className="w-full aspect-square" />
                
                {/* Result Overlay */}
                {result && (
                  <div 
                    className={`absolute inset-0 flex items-center justify-center ${
                      result.allowed 
                        ? 'bg-green-500/95' 
                        : result.status === 'already_redeemed'
                          ? 'bg-yellow-500/95'
                          : result.status === 'wrong_event'
                            ? 'bg-purple-500/95'
                            : 'bg-red-500/95'
                    }`}
                    data-testid="meal-scan-result"
                  >
                    <div className="text-center p-6 max-w-md w-full">
                      <div className="mb-3">
                        {result.allowed ? (
                          <CheckCircle className="h-20 w-20 mx-auto text-white" />
                        ) : result.status === 'already_redeemed' ? (
                          <AlertTriangle className="h-20 w-20 mx-auto text-white" />
                        ) : (
                          <XCircle className="h-20 w-20 mx-auto text-white" />
                        )}
                      </div>
                      <div className="text-3xl font-bold mb-2 text-white">
                        {result.allowed 
                          ? 'ACCESS GRANTED' 
                          : result.status === 'already_redeemed' 
                            ? 'ALREADY SERVED' 
                            : result.status === 'wrong_event'
                              ? 'WRONG EVENT'
                              : result.status === 'not_found'
                                ? 'NOT FOUND'
                                : result.status === 'not_checked_in'
                                  ? 'NOT CHECKED IN'
                                  : result.status === 'not_entitled'
                                    ? 'NOT ENTITLED'
                                    : result.status === 'zone_denied'
                                      ? 'ZONE RESTRICTED'
                                      : 'ACCESS DENIED'}
                      </div>
                      <div className="text-xl text-white/90 mb-4">
                        {result.message}
                      </div>
                      {result.delegate && (
                        <div className="text-white/90 space-y-2">
                          <div className="text-2xl font-semibold">{result.delegate.name}</div>
                          {result.delegate.tier && (
                            <Badge className="bg-white/20 text-white text-lg px-4 py-1">
                              {result.delegate.tier}
                            </Badge>
                          )}
                          {result.delegate.meal_type && result.allowed && (
                            <div className="text-lg mt-2 capitalize">
                              {result.delegate.meal_type} • {result.delegate.dining_zone}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-4 flex justify-center gap-3">
                <Button 
                  variant="outline" 
                  onClick={stopScanner} 
                  className="text-white border-gray-600 hover:bg-gray-700"
                >
                  <CameraOff className="h-4 w-4 mr-2" />
                  Stop Scanner
                </Button>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-200">
              {error}
            </div>
          )}
        </div>

        {/* Stats Sidebar */}
        <div className="w-full lg:w-80 bg-gray-800 p-4 overflow-y-auto">
          {/* Current Meal Indicator */}
          <Card className="bg-orange-600/20 border-orange-500/30 mb-4">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getMealIcon(selectedMealType)}
                  <span className="text-orange-300 font-medium capitalize">{selectedMealType}</span>
                </div>
                <Badge className="bg-orange-500/30 text-orange-300 capitalize">
                  {selectedDiningZone}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Today's Stats
          </h3>
          
          <div className="grid grid-cols-2 gap-3 mb-6">
            <Card className="bg-gray-700/50">
              <CardContent className="pt-3 text-center">
                <div className="text-2xl font-bold text-orange-400">{stats.breakfast}</div>
                <div className="text-xs text-gray-400">Breakfast</div>
              </CardContent>
            </Card>
            <Card className="bg-gray-700/50">
              <CardContent className="pt-3 text-center">
                <div className="text-2xl font-bold text-yellow-400">{stats.lunch}</div>
                <div className="text-xs text-gray-400">Lunch</div>
              </CardContent>
            </Card>
            <Card className="bg-gray-700/50">
              <CardContent className="pt-3 text-center">
                <div className="text-2xl font-bold text-blue-400">{stats.dinner}</div>
                <div className="text-xs text-gray-400">Dinner</div>
              </CardContent>
            </Card>
            <Card className="bg-gray-700/50">
              <CardContent className="pt-3 text-center">
                <div className="text-2xl font-bold text-green-400">{stats.total_today}</div>
                <div className="text-xs text-gray-400">Total Today</div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Scans */}
          <h3 className="text-lg font-semibold mb-3">Recent Scans</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {recentScans.map((scan, i) => (
              <div 
                key={scan.id || i}
                className="bg-gray-700/50 p-2 rounded-lg text-sm"
              >
                <div className="font-medium text-white">{scan.delegate_name}</div>
                <div className="flex justify-between text-gray-400 text-xs">
                  <span className="capitalize">{scan.meal_type}</span>
                  <span>{new Date(scan.scanned_at).toLocaleTimeString()}</span>
                </div>
              </div>
            ))}
            {recentScans.length === 0 && (
              <p className="text-gray-500 text-sm text-center py-4">No scans yet today</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MealScanStation;
